import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Network, Cable, CreditCard, Cctv, MonitorPlay, ServerCog, Rocket, Wrench } from "lucide-react";
import { MaskedLine, Reveal, EASE } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import SectionHeading from "@/components/SectionHeading";
import CTABand from "@/components/CTABand";
import { CAPABILITIES, PROCESS_STEPS, SERVICES, INDUSTRIES, IMAGES, ILLUSTRATIVE_NOTE } from "@/data/site";

const SERVICE_ICONS = [Network, Cable, CreditCard, Cctv, MonitorPlay, ServerCog, Rocket, Wrench];

function FigCaption({ label }) {
  return (
    <div className="flex justify-between gap-4 mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">
      <span>{label}</span>
      <span className="text-signal shrink-0">{ILLUSTRATIVE_NOTE}</span>
    </div>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-ink"
    >
      <motion.div aria-hidden="true" style={{ y: gridY }} className="absolute inset-0 grid-bg" />
      <div
        aria-hidden="true"
        className="absolute -top-40 right-0 w-[60vw] h-[60vw] rounded-full opacity-20 blur-[140px]"
        style={{ background: "radial-gradient(circle, #3EC6F0 0%, transparent 60%)" }}
      />

      <motion.div
        aria-hidden="true"
        style={{ y: imgY }}
        initial={{ opacity: 0, scale: 1.06 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.6, delay: 0.9, ease: EASE }}
        className="absolute right-0 top-24 hidden lg:block w-[42vw] max-w-3xl"
      >
        <div className="clip-frame relative">
          <img
            src={IMAGES.serverRoom}
            alt=""
            className="w-full h-[70vh] object-cover img-field"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <FigCaption label="Illustrative image — server & network infrastructure" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 pb-12 md:pb-16 pt-32 md:pt-36">
        <MaskedLine delay={0.1}>
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-signal mb-6">
            LEDOS Enterprise Solutions
          </p>
        </MaskedLine>

        <h1 className="font-display font-black tracking-tighter leading-[0.92] text-[11.5vw] sm:text-[8.5vw] lg:text-[5.6vw]">
          <MaskedLine delay={0.25}>TECHNOLOGY</MaskedLine>
          <MaskedLine delay={0.38}>
            <span>
              INSTALLED. <span className="text-signal">CONNECTED.</span>
            </span>
          </MaskedLine>
          <MaskedLine delay={0.51}>
            <span>
              CONFIGURED. <span className="text-signal">SUPPORTED.</span>
            </span>
          </MaskedLine>
        </h1>

        <div className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <MaskedLine delay={0.7} className="max-w-xl">
            <p className="text-base md:text-lg leading-relaxed text-zinc-400">
              LEDOS Enterprise Solutions delivers professional IT infrastructure, technology
              deployment and field support services — from network cabling, servers and switches
              to ATMs, POS systems, surveillance and digital signage.
            </p>
          </MaskedLine>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="flex flex-col sm:flex-row gap-4 shrink-0"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-discuss-project"
              className="group inline-flex items-center justify-center gap-2 bg-signal text-black font-mono text-xs font-bold tracking-[0.15em] uppercase px-8 py-5 hover:-translate-y-1 transition-transform duration-300"
            >
              Discuss Your Project
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-explore-services"
              className="inline-flex items-center justify-center gap-2 border border-line text-white font-mono text-xs tracking-[0.15em] uppercase px-8 py-5 hover:border-signal hover:text-signal transition-colors duration-300"
            >
              Explore Our Services
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}

function OnePartner() {
  return (
    <section data-testid="one-partner-section" className="bg-ink py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          <div className="lg:sticky lg:top-32">
            <SectionHeading
              index="01"
              eyebrow="Why LEDOS"
              title="One Partner for Your Technology Infrastructure"
            />
            <Reveal delay={0.1}>
              <p className="text-base leading-relaxed text-zinc-400 max-w-lg">
                A typical technology deployment touches cabling, networking, hardware,
                configuration and support — often coordinated across several separate
                installation vendors. LEDOS brings those pieces together. We can support
                multiple components of your technology deployment, so you spend less time
                managing contractors and more time running your business.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <ul className="mt-10 space-y-0 border-t border-line">
                {[
                  "Infrastructure, cabling and equipment under one roof",
                  "Consistent installation standards across every component",
                  "Post-installation support from the team that did the work",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-4 py-4 border-b border-line font-mono text-xs tracking-[0.1em] uppercase text-zinc-300"
                  >
                    <span className="w-2 h-2 bg-signal shrink-0" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="group">
            <div className="clip-frame-alt relative">
              <img
                src={IMAGES.networkSwitch}
                alt="Network switch with structured cabling"
                className="w-full h-[60vh] md:h-[75vh] object-cover img-field"
              />
              <div className="absolute inset-0 bg-black/30" />
            </div>
            <FigCaption label="Illustrative image — network & connectivity" />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function WhatWeDo() {
  return (
    <section data-testid="what-we-do-section" className="bg-panel border-y border-line py-24 md:py-36 relative overflow-hidden">
      <div aria-hidden="true" className="absolute inset-0 grid-bg-fine opacity-60" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <SectionHeading
          index="02"
          eyebrow="What We Do"
          title="Eight Capability Areas. One Accountable Team."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
          {SERVICES.map((s, i) => {
            const Icon = SERVICE_ICONS[i];
            return (
              <Reveal key={s.slug} delay={(i % 4) * 0.08} className="bg-panel">
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group flex flex-col h-full p-8 min-h-[280px] hover:bg-surface transition-colors duration-500"
                >
                  <div className="flex items-start justify-between mb-10">
                    <span className="font-display text-4xl font-black text-outline-signal">
                      {s.num}
                    </span>
                    <Icon className="w-6 h-6 text-zinc-600 group-hover:text-signal transition-colors duration-500" />
                  </div>
                  <h3 className="font-display text-xl font-bold tracking-tight text-white group-hover:text-signal transition-colors duration-500 mb-3">
                    {s.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-zinc-500 flex-1">{s.short}</p>
                  <span className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-600 group-hover:text-signal transition-colors duration-500">
                    View Service
                    <ArrowRight className="w-3 h-3 transition-transform duration-500 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HowWeWork() {
  return (
    <section data-testid="how-we-work-section" className="bg-ink py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <SectionHeading
          index="03"
          eyebrow="How We Work"
          title="A Disciplined Process, Every Site, Every Time"
        />
        <div className="border-t border-line">
          {PROCESS_STEPS.map((step, i) => (
            <Reveal key={step.num} delay={i * 0.05}>
              <div
                data-testid={`process-step-${step.num}`}
                className="group grid md:grid-cols-12 gap-4 md:gap-8 items-baseline py-8 md:py-10 border-b border-line hover:bg-panel transition-colors duration-500 px-2 md:px-6"
              >
                <span className="md:col-span-2 font-display text-5xl md:text-7xl font-black tracking-tighter text-outline group-hover:text-signal group-hover:[-webkit-text-stroke:0px] transition-all duration-500">
                  {step.num}
                </span>
                <h3 className="md:col-span-3 font-display text-2xl md:text-3xl font-bold tracking-tight text-white">
                  {step.title}
                </h3>
                <p className="md:col-span-7 text-base leading-relaxed text-zinc-400 max-w-xl">
                  {step.text}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function RolloutScale() {
  return (
    <section data-testid="rollout-scale-section" className="bg-panel border-y border-line py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <Reveal className="group order-2 lg:order-1">
            <div className="clip-frame relative">
              <img
                src={IMAGES.deployment}
                alt="Illustrative image of technology deployment work"
                className="w-full h-[55vh] md:h-[65vh] object-cover img-field"
              />
              <div className="absolute inset-0 bg-black/30" />
            </div>
            <FigCaption label="Illustrative image — technology deployment" />
          </Reveal>
          <div className="order-1 lg:order-2">
            <SectionHeading
              index="04"
              eyebrow="Project Scale"
              title="From a Single Installation to a Complete Technology Rollout"
            />
            <Reveal delay={0.1}>
              <p className="text-base leading-relaxed text-zinc-400 mb-6">
                Some projects are a single camera, a single POS terminal or a single rack.
                Others are coordinated deployments across multiple locations — new-site
                buildouts, hardware refreshes, network, signage or payment technology rollouts.
              </p>
              <p className="text-base leading-relaxed text-zinc-400 mb-10">
                LEDOS is built for both. Every project moves through the same disciplined
                structure: planning, site readiness, installation, configuration, testing,
                documentation, issue resolution and completion.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/services/technology-rollouts"
                data-testid="rollout-section-link"
                className="group inline-flex items-center gap-2 border border-line text-white font-mono text-xs tracking-[0.15em] uppercase px-6 py-4 hover:border-signal hover:text-signal transition-colors duration-300"
              >
                About Our Rollout Services
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function IndustriesPreview() {
  return (
    <section data-testid="industries-preview-section" className="bg-ink py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <SectionHeading
          index="05"
          eyebrow="Environments"
          title="Built for the Places Business Happens"
        />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
          {INDUSTRIES.slice(0, 8).map((ind, i) => (
            <Reveal key={ind.name} delay={(i % 4) * 0.06} className="bg-ink">
              <div
                data-testid={`industry-tile-${ind.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group relative aspect-[4/3] overflow-hidden"
              >
                <img
                  src={ind.image}
                  alt={ind.name}
                  className="absolute inset-0 w-full h-full object-cover img-field opacity-50 group-hover:opacity-70 transition-opacity duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <p className="absolute bottom-4 left-4 right-4 font-mono text-xs tracking-[0.15em] uppercase text-white">
                  {ind.name}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <Link
            to="/industries"
            data-testid="industries-preview-link"
            className="mt-10 inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-signal transition-colors duration-300"
          >
            View All Environments
            <ArrowRight className="w-3 h-3" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  useEffect(() => {
    document.title = "LEDOS Enterprise Solutions — Technology Installed. Connected. Configured. Supported.";
  }, []);

  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee items={CAPABILITIES} />
      <OnePartner />
      <WhatWeDo />
      <HowWeWork />
      <RolloutScale />
      <IndustriesPreview />
      <CTABand />
    </main>
  );
}
